import type { ReverifySlice } from "./steps";

/** What pressing the Submit step's action does. */
export type SubmitButtonAction = "submit" | "verify" | "restart" | "none";

export interface SubmitButtonState {
    label: string;
    status: "idle" | "pending" | "success" | "error";
    disabled: boolean;
    /** The retry glyph follows the label (Retry, Verify Again). */
    retryIcon: boolean;
    /** The long right arrow follows the label (Add New Address). */
    arrowIcon?: boolean;
    action: SubmitButtonAction;
}

/** Every label the action can show, so the button's width never jumps. */
export const SUBMIT_BUTTON_RESERVE_LABELS: string[] = [
    "Submit Address for Review",
    "Add Address",
    "Submitting",
    "Submitted",
    "Adding...",
    "Added",
    "Submit Failed",
    "Retry",
    "Verification Expired",
    "Verify Again",
    "Verifying...",
    "Verification Failed",
    "Already Registered",
    "Add New Address",
    "Claim Wallet",
    "Claiming...",
    "Claimed"
];

/**
 * The Submit step's action, as a pure function of the flow's state.
 *
 * - A submit rejected because the address was registered meanwhile (another
 *   member added it after this one was checked at entry) holds "Already
 *   Registered" for 3s, then becomes "Add New Address" + arrow, which clears
 *   the address and returns to step 1. It takes precedence over a re-verify:
 *   verifying again can't fix it.
 * - A failed submit holds "Submit Failed" for 3s (`holdDone` false), then becomes an
 *   enabled "Retry" that resubmits.
 * - A potentially consumed proof holds "Submit Failed" before "Verify Again".
 * - An expired verification proof (`reverify` set) holds "Verification
 *   Expired" for the same 3s, then becomes "Verify Again", which re-runs the
 *   ownership ceremony in place. While it runs the action reads
 *   "Verifying..."; a failed re-verification follows the step 1 pattern
 *   (Verification Failed, then Retry, which re-verifies). On success the
 *   host drops `reverify` and clears the error, so the action is the submit
 *   label again. It never resubmits the expired proof.
 */
export function resolveSubmitButton(args: {
    isSubmitting: boolean;
    submitted: boolean;
    failed: boolean;
    /** The 3s hold after a failure has elapsed. */
    holdDone: boolean;
    automaticEnrollment: boolean;
    reverify: ReverifySlice | null;
    /** The submit was rejected because the address is already registered. */
    alreadyRegistered?: boolean;
    claimRequired?: boolean;
}): SubmitButtonState {
    const { isSubmitting, submitted, failed, holdDone, automaticEnrollment, reverify, alreadyRegistered } = args;
    const inert = (label: string, status: SubmitButtonState["status"]): SubmitButtonState => ({
        label,
        status,
        disabled: true,
        retryIcon: false,
        action: "none"
    });

    // Automatic enrollment adds the address: Add Address, Adding..., Added.
    // A manual-review submission keeps Submitting, Submitted.
    if (isSubmitting)
        return inert(args.claimRequired ? "Claiming..." : automaticEnrollment ? "Adding..." : "Submitting", "pending");
    if (submitted)
        return inert(args.claimRequired ? "Claimed" : automaticEnrollment ? "Added" : "Submitted", "success");

    if (alreadyRegistered) {
        return holdDone
            ? {
                  label: "Add New Address",
                  status: "idle",
                  disabled: false,
                  retryIcon: false,
                  arrowIcon: true,
                  action: "restart"
              }
            : inert("Already Registered", "error");
    }

    if (reverify) {
        if (reverify.status === "verifying") return inert("Verifying...", "pending");
        if (reverify.status === "failed") {
            return reverify.retryReady
                ? { label: "Retry", status: "idle", disabled: false, retryIcon: true, action: "verify" }
                : inert("Verification Failed", "error");
        }
        return holdDone
            ? { label: "Verify Again", status: "idle", disabled: false, retryIcon: true, action: "verify" }
            : inert(reverify.reason === "submit-failed" ? "Submit Failed" : "Verification Expired", "error");
    }

    if (failed) {
        return holdDone
            ? { label: "Retry", status: "idle", disabled: false, retryIcon: true, action: "submit" }
            : inert("Submit Failed", "error");
    }

    return {
        label: args.claimRequired ? "Claim Wallet" : automaticEnrollment ? "Add Address" : "Submit Address for Review",
        status: "idle",
        disabled: false,
        retryIcon: false,
        action: "submit"
    };
}
